import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import DesktopLayout from '../layout/DesktopLayout';
import { useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { useOffers } from './store/useOffers';
import { useLocations } from './store/useLocations';
import { useTiers } from './store/useTiers';
import { useOfferDiscardGuard } from './hooks/useOfferDiscardGuard';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { useOfferManagement, useOfferToastStore } from '../../redux/offers/offerHooks';
import { offerStatusToSaveMode } from '../../services/offerApi';
import { Button, Card, ConfirmDialog, Toggle } from './components/ui';
import { TextField, NumberField, TextArea, Select, RadioGroup, CheckboxGroup, FormSection } from './components/forms';
import CategoryItemPicker from './components/forms/CategoryItemPicker';
import PageHeader from './components/layout/PageHeader';
import Breadcrumb from './components/layout/Breadcrumb';
import { IconTag } from './components/ui/Icons';
import './OfferForm.css';
import type {
  Offer, OfferType, OfferStatus, OfferOrderType, OrderTypeGroup,
  ApplicationLevel, ApplicationMode, OfferTypeConfig,
  OfferSchedule, OfferEligibility, BOGOConfig, FreeItemConfig,
  ComboConfig, ComboSlot, HappyHourConfig, FirstTimeConfig, LoyaltyLinkedConfig,
} from './data/types';
import { getCachedOfferMenuItems, subscribeOfferMenuItems } from './data/mockMenuItems';
import { offerToDuplicateFormState, type OfferFormFields } from './utils/duplicateOfferForm';

// ── Constants ──

const ADVANCED_ERROR_KEYS = [
  'orderTypes', 'promoCodes', 'recurringDays', 'recurringTime',
  'minCheckSubtotal', 'requiredItems', 'maxRedemptionsTotal', 'maxRedemptionsPerCustomer',
];

const ALL_OFFER_TYPES: { value: OfferType; label: string; levels: ApplicationLevel[] }[] = [
  { value: 'percentage-off', label: 'Percentage Off (%)', levels: ['order', 'item'] },
  { value: 'flat-dollar-off', label: 'Flat Amount Off ($)', levels: ['order', 'item'] },
  // { value: 'bogo', label: 'BOGO (Buy One Get One)', levels: ['item'] },
  { value: 'free-item', label: 'Free Item with Minimum Spend', levels: ['item'] },
  { value: 'free-delivery', label: 'Free Delivery', levels: ['order'] },
  // combo-bundle removed — belongs in Menu Management, not Offers
  // happy-hour removed — use Percentage/Flat Off + Recurring Schedule
  // first-time removed — use "First-time customers only" toggle in Eligibility Rules with any offer type
  // loyalty-linked removed — loyalty rewards belong in Loyalty module
];

// ── Order Type Groups (mapped to shared menus) ──

const POS_ORDER_TYPES: { value: OfferOrderType; label: string }[] = [
  { value: 'dine-in', label: 'Dine-In' },
  { value: 'walk-in', label: 'Walk-In' },
  { value: 'phone', label: 'Phone Order' },
];

const CUSTOMER_ORDER_TYPES: { value: OfferOrderType; label: string }[] = [
  { value: 'online-pickup', label: 'Online Pickup' },
  { value: 'online-delivery', label: 'Online Delivery' },
  { value: 'table-kiosk', label: 'Table Kiosk' },
  { value: 'station-kiosk', label: 'Station Kiosk' },
];

const ALL_POS: OfferOrderType[] = POS_ORDER_TYPES.map((o) => o.value);
const ALL_CUSTOMER: OfferOrderType[] = CUSTOMER_ORDER_TYPES.map((o) => o.value);

// ── Outlet Order Type → OfferOrderType resolution ──
// `selectedBranch.orderTypes` comes from the restaurant details API and contains
// merchant-configured order types like { id, typeName, typeGroup, isEnabled }.
// Only enabled entries with typeGroup `D` belong to POS Orders; `P` or `S` to Customer Orders.

type OutletOrderType = {
  id?: string;
  typeName?: string;
  typeGroup?: string;
  isEnabled?: number | boolean;
};

// typeGroup is the authoritative signal for what an outlet order type means —
// same convention as ItemsPage.tsx's mapOutletOrderTypeToKey: D=dine-in, S=delivery, P=pickup.
function matchOfferOrderType(entry: OutletOrderType): OfferOrderType | null {
  switch ((entry.typeGroup ?? '').toUpperCase()) {
    case 'D': return 'dine-in';
    case 'S': return 'online-delivery';
    case 'P': return 'online-pickup';
    default: return null;
  }
}

function buildOutletOrderTypeIdBySlug(
  selectedBranch: Record<string, any> | null | undefined,
): Partial<Record<OfferOrderType, string>> {
  const raw = selectedBranch?.orderTypes;
  if (!Array.isArray(raw) || raw.length === 0) return {};
  const out: Partial<Record<OfferOrderType, string>> = {};
  raw.forEach((entry: OutletOrderType) => {
    // const enabled = entry?.isEnabled === 1 || entry?.isEnabled === true;
    // if (!enabled) return;
    const group = (entry?.typeGroup ?? '').toUpperCase();
    if (group !== 'D' && group !== 'P' && group !== 'S') return;
    const key = matchOfferOrderType(entry);
    const id = String(entry?.id ?? '').trim();
    if (!key || !id || out[key]) return;
    out[key] = id;
  });
  return out;
}

function buildOutletOrderTypeOptions(
  selectedBranch: Record<string, any> | null | undefined,
): { pos: { value: OfferOrderType; label: string }[]; customer: { value: OfferOrderType; label: string }[] } {
  const raw = selectedBranch?.orderTypes;
  if (!Array.isArray(raw) || raw.length === 0) return { pos: [], customer: [] };

  const posLabelByKey = new Map(POS_ORDER_TYPES.map((o) => [o.value, o.label] as const));
  const customerLabelByKey = new Map(CUSTOMER_ORDER_TYPES.map((o) => [o.value, o.label] as const));

  const posSeen = new Set<OfferOrderType>();
  const customerSeen = new Set<OfferOrderType>();
  const pos: { value: OfferOrderType; label: string }[] = [];
  const customer: { value: OfferOrderType; label: string }[] = [];

  raw.forEach((entry: OutletOrderType) => {
    // const enabled = entry?.isEnabled === 1 || entry?.isEnabled === true;
    // if (!enabled) return;
    const group = (entry?.typeGroup ?? '').toUpperCase();
    const key = matchOfferOrderType(entry);
    if (!key) return;
    const label = (entry?.typeName ?? '').trim();

    if (group === 'D' && posLabelByKey.has(key) && !posSeen.has(key)) {
      posSeen.add(key);
      pos.push({ value: key, label: label || posLabelByKey.get(key)! });
    } else if ((group === 'P' || group === 'S') && customerLabelByKey.has(key) && !customerSeen.has(key)) {
      customerSeen.add(key);
      customer.push({ value: key, label: label || customerLabelByKey.get(key)! });
    }
  });

  return { pos, customer };
}

const APP_LEVEL_OPTIONS = [
  { value: 'order', label: 'Order Level — discount applies to the entire order' },
  { value: 'item', label: 'Item Level — discount applies to specific items' },
];

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public \u2014 shown on all ordering channels', description: 'Appears in the offers list, banners, and pop-ups.' },
  { value: 'hidden', label: 'Hidden \u2014 not shown on any ordering channel', description: 'Works only when the promo code is entered at checkout.' },
];

const DAY_OPTIONS = [
  { value: '0', label: 'Mon' }, { value: '1', label: 'Tue' }, { value: '2', label: 'Wed' },
  { value: '3', label: 'Thu' }, { value: '4', label: 'Fri' }, { value: '5', label: 'Sat' }, { value: '6', label: 'Sun' },
];

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const label = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  return { value: label, label };
});

const APP_MODE_OPTIONS = [
  { value: 'auto-apply', label: 'Auto-Apply' },
  { value: 'manual', label: 'Manual' },
  { value: 'promo-code', label: 'Promo Code' },
];

const REDEMPTION_PERIOD_OPTIONS = [
  { value: 'lifetime', label: 'Per Lifetime' },
  { value: 'day', label: 'Per Day' },
  { value: 'week', label: 'Per Week' },
  { value: 'month', label: 'Per Month' },
];

// ── Default form state ──

function defaultSchedule(): OfferSchedule {
  return { startDate: new Date().toLocaleDateString('en-CA'), endDate: null, noEndDate: true, recurring: null };
}

function defaultEligibility(): OfferEligibility {
  return { firstTimeOnly: false, minCheckSubtotal: null, maxCheckSubtotal: null, requiredItemIds: [], requiredCategoryNames: [], excludedItemIds: [], excludedCategoryNames: [], maxRedemptionsTotal: null, maxRedemptionsPerCustomer: null, redemptionPeriod: 'lifetime' as const };
}

function defaultOffer(): Omit<Offer, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'redemptionCount'> {
  return {
    name: '', description: '', internalNotes: '',
    type: 'percentage-off', discountValue: 0, maxDiscountCap: null, applicationLevel: 'order',
    orderTypes: ['dine-in', 'walk-in', 'phone', 'table-kiosk', 'online-pickup', 'online-delivery', 'station-kiosk'],
    locationScope: 'all', locationIds: [], locationGroupIds: [],
    status: 'draft', isExclusive: true, isHidden: false, origin: 'local' as const,
    typeConfig: null,
    schedule: defaultSchedule(),
    eligibility: defaultEligibility(),
    promoCodes: [], locationOverrides: [],
    // Auto-Apply defaults OFF for new offers — staff/customer must select the offer manually
    applicationModes: { inStore: 'manual', digital: 'manual' },
    permittedRoles: ['cashier', 'manager', 'admin'],
    managerOverrideRequired: false,
  };
}

// ── Helper: check name has at least one alphanumeric character ──
function hasAlphanumeric(s: string): boolean {
  return /[a-zA-Z0-9]/.test(s);
}

function isValidDateString(value: string): boolean {
  if (!value) return false;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900 || year > 2099) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** Keep BOGO junction row UUIDs aligned with item ids when the picker changes. */
function alignBogoRowIds(
  oldItemIds: string[],
  oldRowIds: string[] | undefined,
  newItemIds: string[],
): string[] {
  const oldR = oldRowIds ?? [];
  return newItemIds.map((id) => {
    const idx = oldItemIds.indexOf(id);
    if (idx >= 0 && oldR[idx]) return oldR[idx];
    return '';
  });
}

type OfferFormShape = Omit<Offer, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'redemptionCount'>;

/**
 * Server expects explicit removals on PATCH via `deleteFields` (promo rows, eligibility menu items, BOGO junction rows).
 */
function buildDeleteFields(initialForm: OfferFormShape, currentForm: OfferFormShape): Record<string, string[]> | undefined {
  const del: Record<string, string[]> = {};

  const initialPromoIds = (initialForm.promoCodes ?? []).map((p) => p.id).filter(Boolean);
  const currentPromoIdSet = new Set((currentForm.promoCodes ?? []).map((p) => p.id).filter(Boolean));
  const promoCodeIds = Array.from(new Set(initialPromoIds.filter((id) => !currentPromoIdSet.has(id))));
  if (promoCodeIds.length) del.promoCodeIds = promoCodeIds;

  const itemIds = [
    ...initialForm.eligibility.requiredItemIds.filter((id) => !currentForm.eligibility.requiredItemIds.includes(id)),
    ...initialForm.eligibility.excludedItemIds.filter((id) => !currentForm.eligibility.excludedItemIds.includes(id)),
  ];

  if (initialForm.type === 'free-item' && initialForm.typeConfig?.kind === 'free-item') {
    const initialFreeIds = initialForm.typeConfig.freeItemIds ?? [];
    const currentFreeIds =
      currentForm.type === 'free-item' && currentForm.typeConfig?.kind === 'free-item'
        ? (currentForm.typeConfig.freeItemIds ?? [])
        : [];
    initialFreeIds.filter((id) => !currentFreeIds.includes(id)).forEach((id) => itemIds.push(id));
  }

  const uniqItemIds = Array.from(new Set(itemIds)).filter(Boolean);
  if (uniqItemIds.length) del.itemIds = uniqItemIds;

  const bogoItemIds: string[] = [];
  const bogoConfigIds: string[] = [];

  if (initialForm.type === 'bogo' && initialForm.typeConfig?.kind === 'bogo') {
    const ib = initialForm.typeConfig;
    const buyRows = ib.buyRowIds ?? [];
    const getRows = ib.getRowIds ?? [];
    const currentBogo = currentForm.type === 'bogo' && currentForm.typeConfig?.kind === 'bogo' ? currentForm.typeConfig : null;

    ib.buyItemIds.forEach((itemId, idx) => {
      if (!currentBogo || !currentBogo.buyItemIds.includes(itemId)) {
        const rid = buyRows[idx];
        if (rid) bogoItemIds.push(rid);
      }
    });
    ib.getItemIds.forEach((itemId, idx) => {
      if (!currentBogo || !currentBogo.getItemIds.includes(itemId)) {
        const rid = getRows[idx];
        if (rid) bogoItemIds.push(rid);
      }
    });

    if (initialForm.type === 'bogo' && currentForm.type !== 'bogo' && ib.bogoConfigId) {
      bogoConfigIds.push(ib.bogoConfigId);
    }
  }

  const uniqBogoRows = Array.from(new Set(bogoItemIds)).filter(Boolean);
  if (uniqBogoRows.length) del.bogoItemIds = uniqBogoRows;
  if (bogoConfigIds.length) del.bogoConfigIds = Array.from(new Set(bogoConfigIds));

  return Object.keys(del).length ? del : undefined;
}

/** True when item lists only shrank (no adds, no INCLUDE/EXCLUDE/ALL mode change). */
function isItemEligibilityDeleteOnly(
  initialPayload: Record<string, unknown>,
  currentPayload: Record<string, unknown>,
): boolean {
  const i = initialPayload.itemEligibility as { mode: string; itemIds: string[] } | undefined;
  const c = currentPayload.itemEligibility as { mode: string; itemIds: string[] } | undefined;
  if (!i || !c || i.mode !== c.mode) return false;
  if (i.mode === 'ALL') return false;
  const initialSet = new Set(i.itemIds);
  for (const id of c.itemIds) {
    if (!initialSet.has(id)) return false;
  }
  return c.itemIds.length < i.itemIds.length;
}

function normalizedPromoPayloadList(payload: Record<string, unknown>): Record<string, unknown>[] {
  if (Object.prototype.hasOwnProperty.call(payload, 'promoCode')) {
    return [payload.promoCode as Record<string, unknown>];
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'promoCodes')) {
    return [...((payload.promoCodes as Record<string, unknown>[]) ?? [])];
  }
  return [];
}

/** Remaining promo rows match initial rows with none added/edited — only removals. */
function isPromoDeleteOnly(
  initialPayload: Record<string, unknown>,
  currentPayload: Record<string, unknown>,
): boolean {
  const ia = normalizedPromoPayloadList(initialPayload);
  const ca = normalizedPromoPayloadList(currentPayload);
  if (ca.length >= ia.length) return false;
  const initialStrs = ia.map((x) => JSON.stringify(x));
  const used = new Set<number>();
  for (const row of ca) {
    const s = JSON.stringify(row);
    const idx = initialStrs.findIndex((t, i) => t === s && !used.has(i));
    if (idx < 0) return false;
    used.add(idx);
  }
  return true;
}

function bogoItemCounts(ids: string[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const id of ids) {
    m.set(id, (m.get(id) ?? 0) + 1);
  }
  return m;
}

/** Buy/get lines only removed; BOGO quantities and discount settings unchanged. */
function isBogoItemsDeleteOnly(
  initialPayload: Record<string, unknown>,
  currentPayload: Record<string, unknown>,
): boolean {
  const ib = initialPayload.bogo as Record<string, unknown> | undefined;
  const cb = currentPayload.bogo as Record<string, unknown> | undefined;
  if (!ib || !cb) return false;
  const scalarKeys = ['buyQty', 'getQty', 'getDiscountPercent', 'equalOrLesserValue'] as const;
  for (const k of scalarKeys) {
    if (JSON.stringify(ib[k]) !== JSON.stringify(cb[k])) return false;
  }
  const iBuy = (ib.buyItemIds as string[]) ?? [];
  const cBuy = (cb.buyItemIds as string[]) ?? [];
  const iGet = (ib.getItemIds as string[]) ?? [];
  const cGet = (cb.getItemIds as string[]) ?? [];
  const buyOk = (() => {
    const im = bogoItemCounts(iBuy);
    const cm = bogoItemCounts(cBuy);
    for (const [id, n] of Array.from(cm.entries())) {
      if ((im.get(id) ?? 0) < n) return false;
    }
    return true;
  })();
  const getOk = (() => {
    const im = bogoItemCounts(iGet);
    const cm = bogoItemCounts(cGet);
    for (const [id, n] of Array.from(cm.entries())) {
      if ((im.get(id) ?? 0) < n) return false;
    }
    return true;
  })();
  if (!buyOk || !getOk) return false;
  return cBuy.length < iBuy.length || cGet.length < iGet.length;
}

// ── API update payload: only changed fields ──

function buildUpdatePayload(
  initialForm: OfferFormShape,
  currentForm: OfferFormShape,
  locationId: string,
  currentStatus: OfferStatus,
  initialStatus: OfferStatus,
  maxCap: number | null,
  orderTypeIdBySlug: Partial<Record<OfferOrderType, string>>,
): Record<string, unknown> {
  const initial = buildApiPayload(initialForm, locationId, initialStatus, initialForm.maxDiscountCap ?? null, orderTypeIdBySlug);
  const current = buildApiPayload(currentForm, locationId, currentStatus, maxCap, orderTypeIdBySlug);
  const diff: Record<string, unknown> = { locationId };
  for (const key of Object.keys(current)) {
    if (JSON.stringify(current[key]) !== JSON.stringify(initial[key])) {
      diff[key] = current[key];
    }
  }
  // Fields that were set in the original but are now absent (user cleared them to 0/unlimited)
  // must be explicitly nulled so the backend knows to remove the limit.
  const CLEARABLE_FIELDS = new Set(['per_customer_limit', 'customer_period', 'maxCap']);
  for (const key of Object.keys(initial)) {
    if (CLEARABLE_FIELDS.has(key) && !(key in current)) {
      diff[key] = null;
    }
  }
  const deleteFields = buildDeleteFields(initialForm, currentForm);
  if (deleteFields) {
    diff.deleteFields = deleteFields;
    if (
      Array.isArray(deleteFields.itemIds) &&
      deleteFields.itemIds.length > 0 &&
      isItemEligibilityDeleteOnly(initial, current)
    ) {
      delete diff.itemEligibility;
    }
    if (
      Array.isArray(deleteFields.promoCodeIds) &&
      deleteFields.promoCodeIds.length > 0 &&
      isPromoDeleteOnly(initial, current)
    ) {
      delete diff.promoCode;
      delete diff.promoCodes;
    }
    if (
      Array.isArray(deleteFields.bogoItemIds) &&
      deleteFields.bogoItemIds.length > 0 &&
      currentForm.type === 'bogo' &&
      Object.prototype.hasOwnProperty.call(current, 'bogo') &&
      isBogoItemsDeleteOnly(initial, current)
    ) {
      delete diff.bogo;
    }
  }
  return diff;
}

// ── API payload mapper ──

const OFFER_TYPE_MAP: Record<string, string> = {
  'percentage-off': 'PERCENTAGE',
  'flat-dollar-off': 'FLAT',
  'bogo': 'BOGO',
  'free-item': 'FREE_ITEM',
  'free-delivery': 'FREE_DELIVERY',
  'combo-bundle': 'COMBO',
  'happy-hour': 'HAPPY_HOUR',
  'first-time': 'FIRST_TIME',
  'loyalty-linked': 'LOYALTY_LINKED',
};

const PERIOD_MAP: Record<string, string> = {
  lifetime: 'LIFETIME',
  day: 'DAILY',
  week: 'WEEKLY',
  month: 'MONTHLY',
};

function buildApiPayload(
  form: Omit<Offer, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'redemptionCount'>,
  locationId: string,
  status: OfferStatus,
  maxCap: number | null,
  orderTypeIdBySlug: Partial<Record<OfferOrderType, string>>,
): Record<string, unknown> {
  const autoApply =
    form.applicationModes.inStore === 'auto-apply' ||
    form.applicationModes.digital === 'auto-apply';

  const payload: Record<string, unknown> = {
    
    locationId,
    name: form.name,
    description: form.description,
    internalNotes: form.internalNotes,
    offerType: OFFER_TYPE_MAP[form.type] ?? form.type.toUpperCase(),
    applicationLevel: form.applicationLevel === 'order' ? 0 : 1,
    discountValue: form.discountValue,
    autoApply,
    isExclusive: form.applicationLevel === 'item' ? false : form.isExclusive,
    isOfferVisible: form.applicationLevel === 'item' ? true : !form.isHidden,
    isFirstTimeOnly: form.eligibility.firstTimeOnly,
    requiresManagerApproval: form.managerOverrideRequired,
    startDate: form.schedule.startDate
      ? new Date(form.schedule.startDate).toISOString()
      : null,
    noEndDate: form.schedule.noEndDate,
    endDate: form.schedule.endDate
      ? new Date(form.schedule.endDate).toISOString()
      : null,
    orderTypeIds: form.orderTypes
      .map((slug) => orderTypeIdBySlug[slug])
      .filter((id): id is string => Boolean(id)),
    saveMode: offerStatusToSaveMode(status),
  };

  if (form.type === 'percentage-off') {
    payload.maxDiscountCap = maxCap ?? null;
  }

  payload.minSubtotal = form.eligibility.minCheckSubtotal ?? 0;

  if (form.type === 'free-item' && form.typeConfig?.kind === 'free-item') {
    payload.minSubtotal = form.typeConfig.minSpend;
    payload.noOfFreeItems = form.typeConfig.maxFreeItems;
  }

  if (form.eligibility.maxRedemptionsPerCustomer !== null) {
    payload.per_customer_limit = form.eligibility.maxRedemptionsPerCustomer;
    payload.customer_period =
      PERIOD_MAP[form.eligibility.redemptionPeriod] ?? 'LIFETIME';
  }

  if (form.eligibility.maxRedemptionsTotal !== null && form.type !== 'free-item') {
    payload.maxCap = form.eligibility.maxRedemptionsTotal;
  }

  const hasRequiredItems = form.eligibility.requiredItemIds.length > 0;
  const hasExcludedItems = form.eligibility.excludedItemIds.length > 0;
  payload.itemEligibility = hasRequiredItems
    ? {
        mode: 'INCLUDE',
        itemIds: form.eligibility.requiredItemIds,
      }
    : hasExcludedItems
      ? {
          mode: 'EXCLUDE',
          itemIds: form.eligibility.excludedItemIds,
        }
      : {
          mode: 'ALL',
          itemIds: [],
        };

  if (form.type === 'free-item' && form.typeConfig?.kind === 'free-item') {
    payload.itemEligibility = {
      mode: 'INCLUDE',
      itemIds: form.typeConfig.freeItemIds,
    };
  }

  if (form.typeConfig?.kind === 'bogo') {
    const b = form.typeConfig;
    payload.bogo = {
      buyItemIds: b.buyItemIds,
      buyQty: b.buyQuantity,
      getItemIds: b.getItemIds,
      getQty: b.getQuantity,
      getDiscountPercent: b.getDiscountPercent,
      equalOrLesserValue: b.equalOrLesserValue,
    };
  }

  const recurring = form.schedule.recurring;
  if (recurring) {
    payload.schedule = {
      enabled: true,
      days: recurring.days,
      allDay: recurring.allDay,
      startTime: recurring.startTime,
      endTime: recurring.endTime,
    };
  }

  const mappedPromoCodes = form.promoCodes
    .filter((pc) => pc.code.trim().length > 0)
    .map((pc) => ({
      name: pc.code.trim().toUpperCase(),
      max_usage: pc.singleUse ? 1 : (pc.maxRedemptions ?? 0),
   
    }));

  if (mappedPromoCodes.length === 1) {
    payload.promoCode = mappedPromoCodes[0];
  } else if (mappedPromoCodes.length > 1) {
    payload.promoCodes = mappedPromoCodes;
  }

  return payload;
}

const S = {
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 5,
  },
};

export default function OfferForm() {
  const { id, sourceId } = useParams<{ id?: string; sourceId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const { selectedBranch } = useBranch();
  const {
    createOfferOnServer,
    updateOfferOnServer,
    deleteOfferOnServer,
    fetchOffers,
    fetchOfferByIdOnServer,
    setEditingOfferId,
    offers: { offers: managedOffers, loading: offersLoading },
  } = useOfferManagement();
  const { offers: localOffers, addOffer, updateOffer, deleteOffer, locationGroups } = useOffers();
  const { locations } = useLocations();
  const { tiers } = useTiers();
  const { addToast } = useOfferToastStore();
  const allOffers = managedOffers.length > 0 ? managedOffers : localOffers;

  // Restrict the displayed POS / Customer order types to those enabled for the selected branch.
  const { pos: branchPosOrderTypes, customer: branchCustomerOrderTypes } = useMemo(
    () => buildOutletOrderTypeOptions(selectedBranch as Record<string, any> | null | undefined),
    [selectedBranch],
  );
  const branchAllPos = useMemo(() => branchPosOrderTypes.map((o) => o.value), [branchPosOrderTypes]);
  const branchAllCustomer = useMemo(() => branchCustomerOrderTypes.map((o) => o.value), [branchCustomerOrderTypes]);
  // Map each OfferOrderType slug to the outlet's order type UUID — used when serializing the API payload.
  const orderTypeIdBySlug = useMemo(
    () => buildOutletOrderTypeIdBySlug(selectedBranch as Record<string, any> | null | undefined),
    [selectedBranch],
  );

  const isDuplicateFlow = Boolean(sourceId);
  const isEditing = Boolean(id);

  const existingOffer = isEditing
    ? managedOffers.find((o) => o.id === id) ?? localOffers.find((o) => o.id === id) ?? null
    : null;

  const sourceOfferForDuplicate = useMemo(() => {
    if (!sourceId) return null;
    return (
      managedOffers.find((o) => o.id === sourceId) ?? localOffers.find((o) => o.id === sourceId) ?? null
    );
  }, [sourceId, managedOffers, localOffers]);
  const isActiveOffer = existingOffer?.status === 'active';

  // Block editing expired offers — must restore to draft first
  const isExpired = existingOffer?.status === 'expired';

  // Promo codes already in use by other ACTIVE offers → code (UPPERCASE) → offer name.
  // Used to reject reusing a promo code that is live on another active offer.
  const promoCodesInUse = useMemo(() => {
    const selfId = isEditing ? String(existingOffer?.id ?? id ?? '').trim() : '';
    const inUse = new Map<string, string>();
    for (const o of allOffers) {
      if (selfId && o.id === selfId) continue;
      const codes = Array.isArray(o.promoCodes) ? o.promoCodes : [];
      for (const pc of codes) {
        const code = String(pc?.code ?? '').trim().toUpperCase();
        if (code) inUse.set(code, o.name);
      }
    }
    return inUse;
  }, [allOffers, isEditing, existingOffer, id]);

  // Reset form when route changes (e.g., navigating back to /offers/new)
  const routeKey = location.key + (id || sourceId || 'new');

  const [createModeBaseline, setCreateModeBaseline] = useState<string | null>(null);
  const [duplicateLoadError, setDuplicateLoadError] = useState<string | null>(null);
  const [duplicateFormReady, setDuplicateFormReady] = useState(() => !sourceId);
  const duplicateByIdAttempted = useRef(false);
  const duplicateHydratedKey = useRef<string | null>(null);

  const [form, setForm] = useState<OfferFormFields>(
    existingOffer ? { ...existingOffer } : defaultOffer()
  );

  // Populate form when editing data becomes available
  useEffect(() => {
    if (id && existingOffer) {
      const slugByOrderTypeId: Record<string, OfferOrderType> = {};
      (Object.entries(orderTypeIdBySlug) as [OfferOrderType, string | undefined][])
        .forEach(([slug, otId]) => {
          if (otId) slugByOrderTypeId[otId] = slug;
        });
      const mappedOrderTypes = Array.isArray(existingOffer.orderTypes)
        ? existingOffer.orderTypes
            .map((v) => (slugByOrderTypeId[String(v)] ?? v) as OfferOrderType)
            .filter((slug) => typeof slug === 'string' && /^[a-z-]+$/.test(slug))
        : [];
      setForm({ ...existingOffer, orderTypes: mappedOrderTypes });
      setShowRecurring(!!existingOffer.schedule.recurring);
      setMaxCap(existingOffer.maxDiscountCap ?? null);
    }
  }, [id, existingOffer, orderTypeIdBySlug]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showTypeChangeConfirm, setShowTypeChangeConfirm] = useState(false);
  const [pendingType, setPendingType] = useState<OfferType | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const advancedContentRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const [showRecurring, setShowRecurring] = useState(!!form.schedule.recurring);
  const [showRequiredItems, setShowRequiredItems] = useState(form.eligibility.requiredItemIds.length > 0 || form.eligibility.requiredCategoryNames.length > 0);
  const [showExcludedItems, setShowExcludedItems] = useState(form.eligibility.excludedItemIds.length > 0 || form.eligibility.excludedCategoryNames.length > 0);
  const [showActiveConfirm, setShowActiveConfirm] = useState(false);
  const [pendingSaveStatus, setPendingSaveStatus] = useState<OfferStatus | null>(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);
  const [requirePromoCode, setRequirePromoCode] = useState(

    form.promoCodes.length > 0 || form.applicationModes.inStore === 'promo-code' || form.applicationModes.inStore === 'auto-apply-promo'

  );
  const [maxCap, setMaxCap] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingAction, setSavingAction] = useState<OfferStatus | null>(null);
  // Menu items come from a module-level cache populated by the Offers list page;
  // the form never triggers its own fetch.
  const [menuItems, setMenuItems] = useState<any[]>(() => getCachedOfferMenuItems());
  const [menuItemsLoading, setMenuItemsLoading] = useState(() => getCachedOfferMenuItems().length === 0);
  const categories = useMemo(() => Array.from(new Set(menuItems.map((m) => m.category))), [menuItems]);

  useEffect(() => {
    duplicateByIdAttempted.current = false;
    duplicateHydratedKey.current = null;
    setDuplicateLoadError(null);
    if (!sourceId) setDuplicateFormReady(true);
    else setDuplicateFormReady(false);
  }, [sourceId]);

  // Load source offer into Redux/local lists for `/offers/duplicate/:sourceId`
  useEffect(() => {
    if (!sourceId || sourceOfferForDuplicate) return;
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (!locationId || !token) return;
    void fetchOffers(locationId, token, { force: true }).catch(() => {});
  }, [
    sourceId,
    sourceOfferForDuplicate,
    selectedBranch?.id,
    selectedBranch?.locationId,
    auth?.accessToken,
    auth?.access_token,
    fetchOffers,
  ]);

  useEffect(() => {
    if (!sourceId || sourceOfferForDuplicate) return;
    if (offersLoading) return;
    if (duplicateByIdAttempted.current) return;
    duplicateByIdAttempted.current = true;
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (!locationId || !token) return;
    void fetchOfferByIdOnServer(sourceId, locationId, token).catch((e) => {
      setDuplicateLoadError(e instanceof Error ? e.message : 'Could not load offer to duplicate');
    });
  }, [
    sourceId,
    sourceOfferForDuplicate,
    offersLoading,
    selectedBranch?.id,
    selectedBranch?.locationId,
    auth?.accessToken,
    auth?.access_token,
    fetchOfferByIdOnServer,
  ]);

  // One-time hydrate per duplicate navigation (avoids wiping edits when list refetches)
  useEffect(() => {
    if (!sourceId || !sourceOfferForDuplicate) return;
    const hasBranchData = Object.keys(orderTypeIdBySlug).length > 0;

    if (duplicateHydratedKey.current === sourceId) {
      // Already hydrated — but if branch data just became available, patch orderTypes
      // so UUIDs (from the API response) are resolved to slugs without wiping user edits.
      if (hasBranchData) {
        setForm((prev) => {
          const hasUnresolved = prev.orderTypes.some((v) => !/^[a-z-]+$/.test(String(v)));
          if (!hasUnresolved) return prev;
          const slugByOrderTypeId: Record<string, OfferOrderType> = {};
          (Object.entries(orderTypeIdBySlug) as [OfferOrderType, string | undefined][])
            .forEach(([slug, otId]) => { if (otId) slugByOrderTypeId[otId] = slug; });
          const fixed = prev.orderTypes
            .map((v) => (slugByOrderTypeId[String(v)] ?? v) as OfferOrderType)
            .filter((slug) => /^[a-z-]+$/.test(String(slug)));
          return { ...prev, orderTypes: fixed };
        });
      }
      setDuplicateFormReady(true);
      return;
    }
    duplicateHydratedKey.current = sourceId;
    const dup = offerToDuplicateFormState(sourceOfferForDuplicate);

    // Source offer orderTypes may be server UUIDs — map them back to slugs
    // so buildApiPayload can resolve them via orderTypeIdBySlug.
    const slugByOrderTypeId: Record<string, OfferOrderType> = {};
    (Object.entries(orderTypeIdBySlug) as [OfferOrderType, string | undefined][])
      .forEach(([slug, otId]) => { if (otId) slugByOrderTypeId[otId] = slug; });
    const mappedOrderTypes = Array.isArray(dup.orderTypes)
      ? dup.orderTypes
          .map((v) => (slugByOrderTypeId[String(v)] ?? v) as OfferOrderType)
          .filter((slug) => typeof slug === 'string' && /^[a-z-]+$/.test(slug))
      : [];
    const dupWithSlugs = { ...dup, orderTypes: mappedOrderTypes };

    setForm(dupWithSlugs);
    setCreateModeBaseline(JSON.stringify(dupWithSlugs));
    setShowRecurring(!!dup.schedule.recurring);
    setShowRequiredItems(
      dup.eligibility.requiredItemIds.length > 0 || dup.eligibility.requiredCategoryNames.length > 0,
    );
    setShowExcludedItems(
      dup.eligibility.excludedItemIds.length > 0 || dup.eligibility.excludedCategoryNames.length > 0,
    );
    setRequirePromoCode(
      dup.promoCodes.length > 0 ||
        dup.applicationModes.inStore === 'promo-code' ||
        dup.applicationModes.inStore === 'auto-apply-promo',
    );
    setDuplicateFormReady(true);
  }, [sourceId, sourceOfferForDuplicate, orderTypeIdBySlug]);

  // Plain `/offers/new` reset
  useEffect(() => {
    if (id || sourceId) return;
    setForm(defaultOffer());
    setCreateModeBaseline(null);
  }, [id, sourceId, routeKey]);

  useEffect(() => {
    if (id) setCreateModeBaseline(null);
  }, [id]);

  // On hard refresh the Redux store is empty — redirect handled in the render
  // gate below (line with `<Navigate to="/offers" …/>`).
  const isHardRefreshEdit = isEditing && !existingOffer && !offersLoading && managedOffers.length === 0;

  // Sync with the shared menu-items cache (populated by the Offers list page).
  useEffect(() => {
    const current = getCachedOfferMenuItems();
    if (current.length > 0) {
      setMenuItems(current);
      setMenuItemsLoading(false);
    }
    const unsubscribe = subscribeOfferMenuItems((items) => {
      setMenuItems(items);
      setMenuItemsLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!id) setEditingOfferId(null);
    else setEditingOfferId(id);
  }, [id, setEditingOfferId]);

  // ── Unsaved changes tracking ──
  const [saving, setSaving] = useState(false);
  const initialForm = useMemo(() => {
    if (existingOffer) return JSON.stringify({ ...existingOffer });
    if (createModeBaseline) return createModeBaseline;
    return JSON.stringify(defaultOffer());
  }, [existingOffer, createModeBaseline]);
  const isDirty = JSON.stringify(form) !== initialForm;

  // ── Warn on navigation with unsaved changes (cancel, browser back, sidebar, tab close) ──
  const {
    showDiscardModal,
    handleCancel,
    handleConfirmDiscard,
    handleKeepEditing,
  } = useOfferDiscardGuard(isDirty && !saving, '/offers');

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
  };

  const updateSchedule = <K extends keyof OfferSchedule>(key: K, value: OfferSchedule[K]) => {
    setForm((prev) => {
      const next = { ...prev.schedule, [key]: value };
      // If start date moves past the current end date, clear the end date so the user must re-pick
      if (key === 'startDate' && next.endDate && typeof value === 'string' && next.endDate < value) {
        next.endDate = null;
      }
      return { ...prev, schedule: next };
    });
  };

  const updateEligibility = <K extends keyof OfferEligibility>(key: K, value: OfferEligibility[K]) => {
    setForm((prev) => ({ ...prev, eligibility: { ...prev.eligibility, [key]: value } }));
  };

  // Add a promo code, rejecting duplicates within this offer or codes already
  // live on another ACTIVE offer.
  const handleAddPromoCode = () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    if (!hasAlphanumeric(code)) {
      setPromoError('Promo code must contain at least one letter or number.');
      return;
    }
    if (form.promoCodes.some((c) => c.code.trim().toUpperCase() === code)) {
      setPromoError('This code is already added to this offer.');
      return;
    }
    const conflictOfferName = promoCodesInUse.get(code);
    if (conflictOfferName) {
      setPromoError('This promo code is already used by another offer. Please choose a different code.');
      return;
    }
    update('promoCodes', [
      ...form.promoCodes,
      { id: crypto.randomUUID(), code, description: '', offerId: '', status: 'active' as const, effectiveFrom: null, effectiveUntil: null, singleUse: false, maxRedemptions: null, usageCount: 0, createdAt: new Date().toISOString() },
    ]);
    setPromoInput('');
    setPromoError(null);
  };

  // Handle offer type change with confirmation
  const handleTypeChange = (newType: string) => {
    if (form.typeConfig && newType !== form.type) {
      setPendingType(newType as OfferType);
      setShowTypeChangeConfirm(true);
    } else {
      applyTypeChange(newType as OfferType);
    }
  };

  const applyTypeChange = (newType: OfferType) => {
    let newConfig: OfferTypeConfig = null;
    switch (newType) {
      case 'bogo':
        newConfig = {
          kind: 'bogo',
          buyItemIds: [],
          buyQuantity: 1,
          getItemIds: [],
          getQuantity: 1,
          getDiscountPercent: 100,
          equalOrLesserValue: false,
          excludeItemIds: [],
          buyRowIds: [],
          getRowIds: [],
        };
        break;
      case 'free-item':
        newConfig = { kind: 'free-item', minSpend: 0, freeItemIds: [], maxFreeItems: 1 };
        break;
      case 'first-time':
        newConfig = null; // No type-specific config — the offer type itself implies first-time eligibility
        break;
    }
    // Auto-apply for types that don't need manual/promo modes
    const autoApplyTypes: OfferType[] = ['free-item', 'free-delivery'];
    const modes = autoApplyTypes.includes(newType)
      ? { inStore: 'auto-apply' as ApplicationMode, digital: 'auto-apply' as ApplicationMode }
      : undefined;

    // Auto-restrict order types for delivery-only offers
    const deliveryOrderTypes = newType === 'free-delivery'
      ? { orderTypes: ['online-delivery'] as typeof form.orderTypes }
      : {};

    if (newType !== 'percentage-off') setMaxCap(null);

    setForm((prev) => ({
      ...prev,
      type: newType,
      typeConfig: newConfig,
      discountValue: newType === 'free-delivery' ? 0 : prev.discountValue,
      ...(modes ? { applicationModes: modes } : {}),
      ...deliveryOrderTypes,
    }));
  };

  const computeErrors = useCallback((): Record<string, string> => {
    const e: Record<string, string> = {};

    // Name validation (AC1.1-AC12, E1)
    if (!form.name.trim()) e.name = 'Offer name is required.';
    else if (!hasAlphanumeric(form.name)) e.name = 'Offer name must contain at least one letter or number.';
    else if (form.name.length > 80) e.name = 'Offer name cannot exceed 80 characters.';

    const descriptionRequired = form.applicationLevel === 'order' || form.type === 'free-item' || form.type === 'free-delivery';
    if (descriptionRequired && !form.description.trim()) e.description = 'Description is required.';
    {
      // Duplicate name check (AC12, 1.9-AC3). Use route `id` when store row not hydrated yet.
      const selfOfferId = isEditing ? String(existingOffer?.id ?? id ?? '').trim() : '';
      const duplicate = allOffers.find(
        (o) =>
          o.name.toLowerCase() === form.name.trim().toLowerCase() &&
          (!selfOfferId || o.id !== selfOfferId),
      );
      if (duplicate) e.name = `An offer named "${duplicate.name}" already exists. Choose a different name.`;
    }

    // Discount value validation (E2, E3)
    if (['percentage-off', 'flat-dollar-off', 'first-time'].includes(form.type) && form.discountValue <= 0) {
      e.discountValue = 'Discount value must be greater than zero.';
    }
    if (form.type === 'percentage-off' && form.discountValue > 100) e.discountValue = 'Percentage discount cannot exceed 100%.';

    // Validate against branch-visible slugs only — form.orderTypes may contain stale
    // slugs for order types not configured for this branch, so a raw length=0 check
    // never fires when those ghost values are present.
    const visibleSlugs = new Set([
      ...branchPosOrderTypes.map((o) => o.value),
      ...branchCustomerOrderTypes.map((o) => o.value),
    ]);
    const hasVisibleSelected = visibleSlugs.size > 0
      ? form.orderTypes.some((ot) => visibleSlugs.has(ot))
      : form.orderTypes.length > 0;
    if (!hasVisibleSelected) e.orderTypes = 'At least one order type must be selected.';
    if (form.locationScope === 'specific' && form.locationIds.length === 0) {
      e.locationIds = 'Please select at least one location.';
    }
    if (form.locationScope === 'group' && form.locationGroupIds.length === 0) {
      e.locationGroupIds = 'Please select at least one location group.';
    }
    if (!form.schedule.startDate) e.startDate = 'Start date is required.';
    else if (!isValidDateString(form.schedule.startDate)) e.startDate = 'Please enter a valid date (MM/DD/YYYY).';
    if (!form.schedule.noEndDate && !form.schedule.endDate) e.endDate = 'Please set an end date or enable "No end date".';
    else if (!form.schedule.noEndDate && form.schedule.endDate && !isValidDateString(form.schedule.endDate)) e.endDate = 'Please enter a valid date (MM/DD/YYYY).';
    if (form.schedule.endDate && form.schedule.startDate && isValidDateString(form.schedule.startDate) && isValidDateString(form.schedule.endDate) && form.schedule.endDate < form.schedule.startDate) {
      e.endDate = 'End date must be on or after the start date.';
    }

    // Recurring schedule validation (2.2 AC5)
    if (showRecurring && form.schedule.recurring && form.schedule.recurring.days.length === 0) {
      e.recurringDays = 'Select at least one day when recurring schedule is enabled.';
    }
    // Recurring time window: end must be after start (HH:MM strings sort chronologically)
    if (showRecurring && form.schedule.recurring && !form.schedule.recurring.allDay) {
      const startTime = form.schedule.recurring.startTime || '00:00';
      const endTime = form.schedule.recurring.endTime || '23:45';
      if (endTime <= startTime) {
        e.recurringTime = 'End time must be after the start time.';
      }
    }

    // Min/max check validation (2.3)
    if (form.eligibility.minCheckSubtotal !== null && form.eligibility.minCheckSubtotal <= 0) {
      e.minCheckSubtotal = 'Minimum check subtotal must be greater than $0.00.';
    }

    // Redemption limits (2.6 AC5)
    if (form.eligibility.maxRedemptionsTotal !== null && form.eligibility.maxRedemptionsTotal < 1) {
      e.maxRedemptionsTotal = 'Total redemption limit must be at least 1.';
    }
    if (form.eligibility.maxRedemptionsPerCustomer !== null && form.eligibility.maxRedemptionsPerCustomer < 1) {
      e.maxRedemptionsPerCustomer = 'Per-customer limit must be at least 1.';
    }

    // Required items validation (2.4 AC5) — only for Item Level, and only for types that show the eligibility section
    if (form.applicationLevel === 'item' && form.type !== 'free-item' && form.type !== 'bogo' && showRequiredItems && form.eligibility.requiredItemIds.length === 0) {
      e.requiredItems = 'Select at least one required item, or disable this section.';
    }

    // Promo code reuse: reject any code already live on another ACTIVE offer.
    if (form.promoCodes.length > 0) {
      const conflict = form.promoCodes.find((c) => promoCodesInUse.has(c.code.trim().toUpperCase()));
      if (conflict) {
        e.promoCodes = 'This promo code is already used by another offer. Please choose a different code.';
      }
    }

    // Type-specific validation
    if (form.typeConfig?.kind === 'combo-bundle' && (form.typeConfig.slots?.length ?? 0) < 1) {
      e.bundleItems = 'A combo must include at least 1 category slot.';
    }
    if (form.type === 'bogo' && form.typeConfig?.kind === 'bogo') {
      if (form.typeConfig.buyItemIds.length === 0) {
        e.bogoBuyItems = 'Select at least one Buy item.';
      }
      if (form.typeConfig.getDiscountPercent < 1) {
        e.getDiscountPercent = 'Get Discount % must be at least 1%.';
      } else if (form.typeConfig.getDiscountPercent > 100) {
        e.getDiscountPercent = 'Get Discount % cannot exceed 100%.';
      }
    }
    if (form.type === 'free-item' && form.typeConfig?.kind === 'free-item') {
      if (form.typeConfig.minSpend < 0.01) {
        e.minSpend = 'Minimum spend must be at least $0.01.';
      }
      if (form.typeConfig.freeItemIds.length === 0) {
        e.freeItemIds = 'Select at least one free item.';
      }
    }

    return e;
  }, [form, isEditing, existingOffer, id, allOffers, showRecurring, showRequiredItems, promoCodesInUse, branchPosOrderTypes, branchCustomerOrderTypes]);

  const validate = (): Record<string, string> => {
    const e = computeErrors();
    setErrors(e);
    return e;
  };

  // Clear inline errors the moment the offending field becomes valid. Runs only
  // when errors are already showing (i.e. after a submit attempt) and never adds
  // new errors here — it only drops or refreshes the ones already visible.
  useEffect(() => {
    setErrors((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const fresh = computeErrors();
      const next: Record<string, string> = {};
      let changed = false;
      for (const k of keys) {
        if (fresh[k]) {
          next[k] = fresh[k];
          if (fresh[k] !== prev[k]) changed = true;
        } else {
          changed = true; // this field was corrected → drop its error
        }
      }
      return changed ? next : prev;
    });
  }, [computeErrors]);

  useEffect(() => {
    if (!pendingScrollRef.current || !showAdvanced) return;
    pendingScrollRef.current = false;
    const firstKey = ADVANCED_ERROR_KEYS.find((k) => errors[k]);
    if (!firstKey || !advancedContentRef.current) return;
    requestAnimationFrame(() => {
      const el = advancedContentRef.current?.querySelector<HTMLElement>(`[data-error-key="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [showAdvanced, errors]);

  const executeSave = useCallback(async (status: OfferStatus) => {
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (!locationId) {
      addToast('Select a location before saving', 'error');
      return;
    }
    setSavingAction(status);
    setSaving(true);
    try {
      const editOfferId = isEditing ? String(existingOffer?.id ?? id ?? '').trim() : '';
      if (isEditing && editOfferId) {
        const initialParsed = JSON.parse(initialForm) as typeof form;
        const priorStatus = existingOffer?.status ?? 'draft';
        const payload = buildUpdatePayload(initialParsed, form, locationId, status, priorStatus, maxCap, orderTypeIdBySlug);
        await updateOfferOnServer(editOfferId, locationId, payload, token);
        
        updateOffer(editOfferId, { ...form, status });
        addToast('Offer updated successfully', 'success');
        await fetchOffers(locationId, token, { force: true });
      } else {
        const payload = buildApiPayload(form, locationId, status, maxCap, orderTypeIdBySlug);
        const created = await createOfferOnServer(payload, token);
        addOffer({ ...form, status, ...(created ?? {}) });
        await fetchOffers(locationId, token, { force: true });
        addToast(`Offer created as ${status}`, 'success');
      }
      navigate('/offers');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save offer';
      addToast(msg, 'error');
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  }, [form, maxCap, initialForm, isEditing, id, existingOffer, selectedBranch, orderTypeIdBySlug, auth, createOfferOnServer, updateOfferOnServer, fetchOffers, updateOffer, addOffer, addToast, navigate]);

  const handleDelete = useCallback(async () => {
    if (!existingOffer) return;
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    setDeleting(true);
    try {
      await deleteOfferOnServer(existingOffer.id, locationId, token);
      deleteOffer(existingOffer.id);
      addToast('Offer deleted', 'success');
      navigate('/offers');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to delete offer';
      addToast(msg, 'error');
    } finally {
      setDeleting(false);
    }
  }, [existingOffer, selectedBranch, auth, deleteOfferOnServer, deleteOffer, addToast, navigate]);

  const handleSave = (status: OfferStatus) => {
    // Block save if an active offer's end date has passed while editing
    const today = new Date().toLocaleDateString('en-CA');
    const isNowExpired =
      isEditing &&
      isActiveOffer &&
      !form.schedule.noEndDate &&
      form.schedule.endDate !== null &&
      form.schedule.endDate < today;
    if (isNowExpired) {
      addToast(
        'This offer has expired and can no longer be edited. Restore it to Draft to make changes.',
        'error',
      );
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      if (ADVANCED_ERROR_KEYS.some((k) => validationErrors[k])) {
        setShowAdvanced(true);
        pendingScrollRef.current = true;
      }
      const msg = Object.values(validationErrors)[0];
      addToast(msg ?? 'Please fix the highlighted fields before saving.', 'error');
      return;
    }

    // Active offer edit confirmation (1.7 AC4)
    if (isEditing && isActiveOffer && status === 'active') {
      setPendingSaveStatus(status);
      setShowActiveConfirm(true);
      return;
    }

    executeSave(status);
  };

  // ── Eligibility Summary ──
  // SVG icon components for rule summary
  const SummaryIcon = ({ type, color = 'var(--accent)' }: { type: string; color?: string }) => {
    const s = { flexShrink: 0 as const, marginTop: 2 };
    const p = { stroke: color, strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };
    switch (type) {
      case 'offer': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" {...p} />
          <circle cx="7" cy="7" r="1.5" fill={color} stroke="none" />
        </svg>
      );
      case 'discount': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <line x1="19" y1="5" x2="5" y2="19" {...p} />
          <circle cx="6.5" cy="6.5" r="2.5" {...p} />
          <circle cx="17.5" cy="17.5" r="2.5" {...p} />
        </svg>
      );
      case 'dates': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <rect x="3" y="4" width="18" height="18" rx="2" {...p} />
          <line x1="16" y1="2" x2="16" y2="6" {...p} />
          <line x1="8" y1="2" x2="8" y2="6" {...p} />
          <line x1="3" y1="10" x2="21" y2="10" {...p} />
        </svg>
      );
      case 'recurring': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <polyline points="23 4 23 10 17 10" {...p} />
          <polyline points="1 20 1 14 7 14" {...p} />
          <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" {...p} />
        </svg>
      );
      case 'happyhour': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <path d="M8 2h8l-2 9H10L8 2z" {...p} />
          <path d="M10 11c0 2 2 3 2 5" {...p} />
          <path d="M14 11c0 2-2 3-2 5" {...p} />
          <path d="M9 20h6" {...p} />
          <path d="M10 16h4" {...p} />
          <circle cx="17" cy="5" r="1" fill={color} stroke="none" />
          <circle cx="19" cy="8" r="0.7" fill={color} stroke="none" />
          <circle cx="18.5" cy="3" r="0.7" fill={color} stroke="none" />
        </svg>
      );
      case 'minspend': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <line x1="12" y1="1" x2="12" y2="23" {...p} />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" {...p} />
        </svg>
      );
      case 'required': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" {...p} />
          <polyline points="22 4 12 14.01 9 11.01" {...p} />
        </svg>
      );
      case 'excluded': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <circle cx="12" cy="12" r="10" {...p} />
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" {...p} />
        </svg>
      );
      case 'limit': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <circle cx="12" cy="12" r="10" {...p} />
          <polyline points="12 6 12 12 16 14" {...p} />
        </svg>
      );
      case 'customer': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" {...p} />
          <circle cx="12" cy="7" r="4" {...p} />
        </svg>
      );
      case 'mode': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <circle cx="12" cy="12" r="3" {...p} />
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" {...p} />
        </svg>
      );
      case 'channels': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <rect x="2" y="3" width="20" height="14" rx="2" {...p} />
          <line x1="8" y1="21" x2="16" y2="21" {...p} />
          <line x1="12" y1="17" x2="12" y2="21" {...p} />
        </svg>
      );
      case 'origin': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <path d="M12 2L20 6V11C20 16.52 16.67 21.74 12 23C7.33 21.74 4 16.52 4 11V6L12 2Z" {...p} />
          <path d="M9 12L11 14L15 10" {...p} />
        </svg>
      );
      case 'stacking': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <path d="M12 2L2 7L12 12L22 7L12 2Z" {...p} />
          <path d="M2 17L12 22L22 17" {...p} />
          <path d="M2 12L12 17L22 12" {...p} />
        </svg>
      );
      case 'delivery': return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <path d="M1 3h15v13H1z" {...p} />
          <path d="M16 8h4l3 4v5h-7V8z" {...p} />
          <circle cx="5.5" cy="18.5" r="2.5" {...p} />
          <circle cx="18.5" cy="18.5" r="2.5" {...p} />
        </svg>
      );
      default: return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={s}>
          <circle cx="12" cy="12" r="10" {...p} />
          <line x1="12" y1="8" x2="12" y2="12" {...p} />
          <line x1="12" y1="16" x2="12.01" y2="16" {...p} />
        </svg>
      );
    }
  };

  const summaryRows = useMemo(() => {
    const rows: { iconKey: string; label: string; value: string; warn?: boolean }[] = [];
    const { schedule, eligibility } = form;

    // Offer type & discount
    const typeLabel = ALL_OFFER_TYPES.find((t) => t.value === form.type)?.label || form.type;
    const levelLabel = form.applicationLevel === 'order' ? 'Order Level' : 'Item Level';
    rows.push({ iconKey: 'offer', label: 'Offer', value: `${typeLabel} — ${levelLabel}` });

    if (form.isExclusive && form.applicationLevel !== 'item') {
      rows.push({ iconKey: 'stacking', label: 'Stacking', value: 'Exclusive — cannot combine with other offers' });
    }

    if (form.type === 'free-delivery') {
      rows.push({ iconKey: 'delivery', label: 'Benefit', value: 'Delivery fee waived' });
    }

    if (form.discountValue > 0 && !['combo-bundle', 'bogo', 'free-item', 'free-delivery'].includes(form.type)) {
      const suffix = form.type === 'flat-dollar-off' ? `$${form.discountValue} off` : `${form.discountValue}% off`;
      rows.push({ iconKey: 'discount', label: 'Discount', value: suffix });
    }

    // Schedule
    if (schedule.startDate) {
      const isPast = schedule.startDate < new Date().toLocaleDateString('en-CA');
      const dateStr = schedule.noEndDate
        ? `${schedule.startDate} → No end date`
        : schedule.endDate
          ? `${schedule.startDate} → ${schedule.endDate}`
          : `${schedule.startDate} → No end date`;
      rows.push({ iconKey: 'dates', label: 'Dates', value: dateStr, warn: isPast });
    }

    if (schedule.recurring) {
      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const dayStr = schedule.recurring.days.map((d) => dayNames[d]).join(', ');
      const timeStr = schedule.recurring.allDay ? 'All Day' : `${schedule.recurring.startTime} – ${schedule.recurring.endTime}`;
      rows.push({ iconKey: 'recurring', label: 'Recurring', value: `${dayStr} · ${timeStr}` });
    }


    // Eligibility
    const minSpendHiddenForItemLevel =
      form.applicationLevel === 'item' &&
      (form.type === 'percentage-off' || form.type === 'flat-dollar-off');
    if (eligibility.minCheckSubtotal && form.type !== 'bogo' && !minSpendHiddenForItemLevel) {
      rows.push({ iconKey: 'minspend', label: 'Min Spend', value: `$${eligibility.minCheckSubtotal}` });
    }

    if (eligibility.requiredItemIds.length > 0) {
      const names = menuItemsLoading
        ? ['Loading items…']
        : eligibility.requiredItemIds.map((id) => menuItems.find((m) => m.id === id)?.name).filter(Boolean) as string[];
      if (names.length > 0) {
        const preview = names.slice(0, 3).join(', ');
        const value = names.length > 3 ? `${preview} ... and ${names.length - 3} more` : preview;
        rows.push({ iconKey: 'required', label: 'Required', value });
      }
    }

    if (eligibility.excludedItemIds.length > 0) {
      const names = menuItemsLoading
        ? ['Loading items…']
        : eligibility.excludedItemIds.map((id) => menuItems.find((m) => m.id === id)?.name).filter(Boolean) as string[];
      if (names.length > 0) {
        const preview = names.slice(0, 3).join(', ');
        const value = names.length > 3 ? `${preview} ... and ${names.length - 3} more` : preview;
        rows.push({ iconKey: 'excluded', label: 'Excluded', value });
      }
    }

    // Redemption limits
    if (eligibility.maxRedemptionsTotal) {
      rows.push({ iconKey: 'limit', label: 'Total Limit', value: `${eligibility.maxRedemptionsTotal} redemptions` });
    }
    if (eligibility.maxRedemptionsPerCustomer) {
      const periodLabel = eligibility.redemptionPeriod === 'lifetime' ? '' : ` / ${eligibility.redemptionPeriod}`;
      rows.push({ iconKey: 'customer', label: 'Per Customer', value: `${eligibility.maxRedemptionsPerCustomer}${periodLabel}` });
    }

    if (eligibility.firstTimeOnly) {
      rows.push({ iconKey: 'customer', label: 'Audience', value: 'First-time customers only' });
    }

    // Application mode
    const mode = form.applicationModes.inStore;
    // if (mode && mode !== 'auto-apply') {
    //   const modeLabels: Record<string, string> = { manual: 'Manual', 'promo-code': 'Promo Code' };
    //   rows.push({ iconKey: 'mode', label: 'Apply Mode', value: modeLabels[mode] || mode });
    // }
    if (mode === 'auto-apply' || mode === 'auto-apply-promo') {
 
      rows.push({ iconKey: 'mode', label: 'Apply Mode', value: 'Auto-Apply' });
 
    } else {
 
      rows.push({ iconKey: 'mode', label: 'Apply Mode', value: 'Staff selects from POS' });
 
    }
 
    if (requirePromoCode) {
 
      rows.push({ iconKey: 'mode', label: 'Promo Code', value: form.promoCodes.length > 0 ? `${form.promoCodes.length} code${form.promoCodes.length > 1 ? 's' : ''}` : 'Enabled (add codes)' });
 
    }

    // Channels — build a comprehensive label map: slug→name AND UUID→name
    const orderTypeLabels: Record<string, string> = {
      'dine-in': 'Dine-In', 'walk-in': 'Walk-In', 'phone': 'Phone Order',
      'online-pickup': 'Online Pickup', 'online-delivery': 'Online Delivery',
      'table-kiosk': 'Table Kiosk', 'station-kiosk': 'Station Kiosk',
    };
    [...branchPosOrderTypes, ...branchCustomerOrderTypes].forEach(({ value: slug, label }) => {
      orderTypeLabels[slug] = label;
      const uuid = orderTypeIdBySlug[slug as OfferOrderType];
      if (uuid) orderTypeLabels[uuid] = label;
    });
    const channels = form.orderTypes
      .map((ot) => orderTypeLabels[ot])
      .filter((name): name is string => Boolean(name));
    if (channels.length > 0 && channels.length < 7) {
      rows.push({ iconKey: 'channels', label: 'Channels', value: channels.join(', ') });
    }

    return rows;
  }, [form, branchPosOrderTypes, branchCustomerOrderTypes, orderTypeIdBySlug]);

  // ── Conflict detection ──
  const conflicts = useMemo(() => {
    const c: string[] = [];
    const { requiredCategoryNames, excludedCategoryNames, requiredItemIds, excludedItemIds } = form.eligibility;
    const catOverlap = requiredCategoryNames.filter((r) => excludedCategoryNames.includes(r));
    if (catOverlap.length > 0) c.push(`Category conflict: "${catOverlap.join(', ')}" is both required and excluded.`);
    const itemOverlap = requiredItemIds.filter((r) => excludedItemIds.includes(r));
    if (itemOverlap.length > 0) c.push(`Item conflict: items are both required and excluded.`);
    return c;
  }, [form.eligibility]);

  // ── Promo code mode warnings ──
  const promoCodeWarning = useMemo(() => {
    if (form.applicationModes.inStore === 'promo-code' && form.promoCodes.length === 0) {
      return null; // Warning is shown inline in the promo code section instead
    }
    return null;
  }, [form.applicationModes, form.promoCodes]);

  // Narrow eligibility advisory
  const narrowAdvisory = useMemo(() => {
    let restrictionCount = 0;
    if (form.eligibility.requiredCategoryNames.length > 0 || form.eligibility.requiredItemIds.length > 0) restrictionCount++;
    if (form.eligibility.excludedCategoryNames.length > 0 || form.eligibility.excludedItemIds.length > 0) restrictionCount++;
    if (form.eligibility.minCheckSubtotal) restrictionCount++;
    if (form.eligibility.maxRedemptionsTotal && form.eligibility.maxRedemptionsTotal < 10) restrictionCount++;
    if (form.eligibility.maxRedemptionsPerCustomer === 1) restrictionCount++;
    if (form.schedule.recurring) restrictionCount++;
    if (form.locationScope === 'specific' || form.locationScope === 'group') restrictionCount++;
    return restrictionCount >= 4 ? 'This offer has a narrow eligibility window. Few customers may qualify.' : null;
  }, [form]);

  // ── Section step label helper ──
  const SectionLabel = ({ title }: { step?: number; title: string }) => (
    <div className="offer-form-section-label-wrap">
      <span className="offer-form-section-label">{title}</span>
    </div>
  );

  if (isDuplicateFlow && duplicateLoadError) {
    return (
      <DesktopLayout>
        <div className="offer-form-page">
          <Card padding={28}>
            <div className="offer-form-expired-card">
              <div className="offer-form-expired-title">Could not duplicate offer</div>
              <div className="offer-form-expired-desc">{duplicateLoadError}</div>
              <Button variant="ghost" onClick={() => navigate('/offers')}>Back to Offers</Button>
            </div>
          </Card>
        </div>
      </DesktopLayout>
    );
  }

  if (isDuplicateFlow && (!sourceOfferForDuplicate || !duplicateFormReady)) {
    return (
      <DesktopLayout>
        <div className="offer-form-page">
          <Card padding={28}>
            <div className="offer-form-expired-card">
              <div className="offer-form-expired-title">Loading offer…</div>
              <div className="offer-form-expired-desc">
                Preparing a copy you can edit and save as a new offer.
              </div>
              <Button variant="ghost" onClick={() => navigate('/offers')}>Back to Offers</Button>
            </div>
          </Card>
        </div>
      </DesktopLayout>
    );
  }

  if (id && !existingOffer) {
    if (isHardRefreshEdit) {
      return <Navigate to="/offers" replace />;
    }
    return (
      <DesktopLayout>
        <div className="offer-form-page">
          <Card padding={28}>
            <div className="offer-form-expired-card">
              <div className="offer-form-expired-title">Loading offer…</div>
              <div className="offer-form-expired-desc">
                Fetching latest offer details for editing.
              </div>
              <Button variant="ghost" onClick={() => navigate('/offers')}>Back to Offers</Button>
            </div>
          </Card>
        </div>
      </DesktopLayout>
    );
  }

  if (isExpired) {
    return (
      <div className="offer-form-expired-wrap">
        <Breadcrumb items={[{ label: 'Offers', to: '/offers' }, { label: 'Edit Offer' }]} />
        <Card padding={28}>
          <div className="offer-form-expired-card">
            <div className="offer-form-expired-title">
              Cannot Edit Expired Offer
            </div>
            <div className="offer-form-expired-desc">
              This offer has expired. To make changes, restore it to Draft status first from the offer detail page.
            </div>
            <Button variant="primary" onClick={() => navigate(`/offers/${id}`)}>Go to Offer Detail</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <DesktopLayout>
    <div className="offer-form-page">
      <Breadcrumb
        items={[
          { label: 'Offers', to: '/offers' },
          {
            label: isEditing ? 'Edit Offer' : isDuplicateFlow ? 'Duplicate Offer' : 'Create Offer',
          },
        ]}
      />
      <PageHeader
        icon={<IconTag size={24} color="rgba(255,255,255,0.95)" />}
        title={
          isEditing
            ? `Edit: ${existingOffer?.name}`
            : isDuplicateFlow
              ? `Duplicate: ${sourceOfferForDuplicate?.name ?? 'Offer'}`
              : 'Create New Offer'
        }
        subtitle={
          isEditing
            ? 'Modify offer configuration.'
            : isDuplicateFlow
              ? 'Review the copy, adjust if needed, then save to create a new offer.'
              : 'Define a new promotion, discount, or deal.'
        }
      />

      {isActiveOffer && (
        <div className="offer-form-active-banner">
          This offer is currently <strong>Active</strong>. <strong>Offer Type</strong> and <strong>Application Level</strong> can't be changed while active. Other changes take effect immediately on all order types.
        </div>
      )}

      {/* ═══ Card 1: Basic Info + Offer Type (Story 1.1) ═══ */}
      <Card>
        <SectionLabel step={1} title="Basic Information" />
        <p className="offer-form-section-desc">Name your offer and choose the discount type.</p>

        <div className="offer-form-col-gap-16">
          <TextField
            label="Offer Name"
            value={form.name}
            onChange={(v) => update('name', v)}
            maxLength={80}
            error={errors.name}
            required
          />
          <TextArea
            label="Description"
            value={form.description}
            onChange={(v) => update('description', v)}
            maxLength={250}
            rows={2}
            error={errors.description}
            required={form.applicationLevel === 'order' || form.type === 'free-item' || form.type === 'free-delivery'}
            helpText="Displayed to customers on the app and kiosk."
          />

          <div className="offer-form-divider" />

          <RadioGroup
            label="Application Level"
            value={form.applicationLevel}
            onChange={(v) => {
              const level = v as ApplicationLevel;
              const availableTypes = ALL_OFFER_TYPES.filter((t) => t.levels.includes(level));
              const currentStillValid = availableTypes.some((t) => t.value === form.type);
              setForm((prev) => ({
                ...prev,
                applicationLevel: level,
                ...(currentStillValid ? {} : { type: availableTypes[0].value as OfferType, typeConfig: null, discountValue: 0 }),
                ...(level === 'item' ? {
                  isExclusive: false,
                  isHidden: false,
                  eligibility: {
                    ...prev.eligibility,
                    firstTimeOnly: false,
                    maxRedemptionsPerCustomer: null,
                  },
                } : {}),
              }));
            }}
            options={APP_LEVEL_OPTIONS}
            disabled={isActiveOffer}
          />

          <div className="offer-form-grid-two">
            <Select
              label="Offer Type"
              value={form.type}
              onChange={handleTypeChange}
              options={ALL_OFFER_TYPES.filter((t) => t.levels.includes(form.applicationLevel)).map(({ value, label }) => ({ value, label }))}
              required
              disabled={isActiveOffer}
            />
            {form.type === 'free-delivery' && (
              <p style={{ gridColumn: '1 / -1', margin: '2px 0 0', fontSize: 'var(--fs-small)', color: 'var(--orange)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '1em' }}>⚠</span>
                Offering Free Delivery may require you to cover the delivery charges.
              </p>
            )}
            {/* Discount value — shown for percentage, flat, happy-hour, first-time, loyalty-linked */}
            {['percentage-off', 'flat-dollar-off', 'first-time'].includes(form.type) && (
              <NumberField
                label="Discount Value"
                value={form.discountValue}
                placeholder="0"
                onChange={(v) => {
                  update('discountValue', Number(v));
                  if (form.type === 'percentage-off' && Number(v) > 100) {
                    setErrors((prev) => ({ ...prev, discountValue: 'Discount value must be ≤ 100%.' }));
                  }
                }}
                prefix={form.type === 'flat-dollar-off' ? '$' : undefined}
                suffix={form.type !== 'flat-dollar-off' ? '%' : undefined}
                min={0.01}
                step={0.01}
                maxIntegerDigits={3}
                maxDecimals={2}
                error={errors.discountValue}
                required
              />
            )}
            {form.type === 'percentage-off' && (
              <NumberField
                label="Max Discount Cap ($)"
                value={maxCap ?? ''}
                onChange={(v) => setMaxCap(v === '' || v === null ? null : Number(v))}
                prefix="$"
                min={0}
                maxIntegerDigits={4}
                maxDecimals={2}
                helpText="Optional: cap the maximum dollar amount discounted."
              />
            )}
          </div>
        </div>
      </Card>

      {/* ═══ Card 2: Type-Specific Configuration ═══ */}
      {form.typeConfig && (
        <Card>

        {form.type === 'bogo' && form.typeConfig?.kind === 'bogo' && (
          <FormSection title="BOGO Configuration" description="Define buy and get items. You can select multiple items for each.">
            <CategoryItemPicker
              mode="item"
              menuItems={menuItems}
              selectedIds={form.typeConfig.buyItemIds}
              onChange={(ids) => {
                const prev = form.typeConfig as BOGOConfig;
                const buyRowIds = alignBogoRowIds(prev.buyItemIds, prev.buyRowIds, ids);
                update('typeConfig', { ...prev, buyItemIds: ids, buyRowIds });
                setErrors((prev) => { const next = { ...prev }; delete next.bogoBuyItems; return next; });
              }}
              label="Buy Item(s)"
              error={errors.bogoBuyItems}
            />
            <NumberField
              label="Buy Quantity (each)"
              value={form.typeConfig.buyQuantity}
              placeholder="0"
              onChange={(v) => update('typeConfig', { ...form.typeConfig as BOGOConfig, buyQuantity: Number(v) })}
              min={1}
              helpText="Quantity required per selected item."
            />

            <div className="offer-form-divider" />

            <CategoryItemPicker
              mode="item"
              menuItems={menuItems}
              selectedIds={form.typeConfig.getItemIds}
              onChange={(ids) => {
                const prev = form.typeConfig as BOGOConfig;
                const getRowIds = alignBogoRowIds(prev.getItemIds, prev.getRowIds, ids);
                update('typeConfig', { ...prev, getItemIds: ids, getRowIds });
              }}
              label="Get Item(s)"
            />
            <NumberField
              label="Get Quantity (each)"
              value={form.typeConfig.getQuantity}
              placeholder="0"
              onChange={(v) => update('typeConfig', { ...form.typeConfig as BOGOConfig, getQuantity: Number(v) })}
              min={1}
              helpText="Quantity given per selected item."
            />
            <NumberField
              label="Get Discount (%)"
              value={form.typeConfig.getDiscountPercent}
              placeholder="1"
              onChange={(v) => {
                update('typeConfig', { ...form.typeConfig as BOGOConfig, getDiscountPercent: Number(v) });
                setErrors((prev) => { const next = { ...prev }; delete next.getDiscountPercent; return next; });
              }}
              min={1}
              max={100}
              maxIntegerDigits={3}
              error={errors.getDiscountPercent}
              required
              suffix="%"
              helpText="100% = free, 50% = half off. Must be at least 1%."
            />

            <div className="offer-form-divider" />

            {/* Equal or lesser value constraint */}
            <div className="offer-form-row-between">
              <div>
                <span className="offer-form-text-13-primary">Equal or lesser value only</span>
                <div className="offer-form-text-11-muted mt-2">Free/discounted item must cost the same or less than the purchased item.</div>
              </div>
              <Toggle
                checked={form.typeConfig.equalOrLesserValue ?? false}
                onChange={(v) => update('typeConfig', { ...form.typeConfig as BOGOConfig, equalOrLesserValue: v })}
              />
            </div>

          </FormSection>
        )}

        {form.type === 'free-item' && form.typeConfig?.kind === 'free-item' && (
          <FormSection title="Free Item Configuration" description="Set minimum spend and select free item(s).">
            <NumberField
              label="Minimum Spend"
              value={form.typeConfig.minSpend}
              placeholder="0.00"
              onChange={(v) => {
                update('typeConfig', { ...form.typeConfig as FreeItemConfig, minSpend: Number(v) });
                setErrors((prev) => { const next = { ...prev }; delete next.minSpend; return next; });
              }}
              prefix="$"
              min={0.01}
              step={0.01}
              maxDecimals={2}
              error={errors.minSpend}
              required
              helpText="Customer must spend at least this amount to qualify. Minimum $0.01."
            />
            <CategoryItemPicker
              mode="item"
              menuItems={menuItems}
              selectedIds={form.typeConfig.freeItemIds}
              onChange={(ids) => {
                update('typeConfig', { ...form.typeConfig as FreeItemConfig, freeItemIds: ids });
                setErrors((prev) => { const next = { ...prev }; delete next.freeItemIds; return next; });
              }}
              label="Free Item(s)"
              error={errors.freeItemIds}
            />
            <NumberField
              label="Max Free Items per Order"
              value={form.typeConfig.maxFreeItems}
              onChange={(v) => update('typeConfig', { ...form.typeConfig as FreeItemConfig, maxFreeItems: Math.max(1, Number(v) || 1) })}
              min={1}
            />
          </FormSection>
        )}

        {/* combo-bundle removed — belongs in Menu Management */}

        {/* happy-hour removed — use Percentage/Flat Off + Recurring Schedule instead */}

        {/* First-Time: no type-specific config needed — the offer type itself implies first-time eligibility */}

        {/* loyalty-linked removed — belongs in Loyalty module */}

        </Card>
      )}

      {/* ═══ Card 2b: Offer Limits ═══ */}
      {form.type !== 'free-item' && form.type !== 'bogo' && (
        <Card>
          <FormSection title="Offer Limits" description="Control spending thresholds, item eligibility, and usage caps.">
            {/* Min Subtotal — hidden for item-level percentage/flat offers */}
            {!(form.applicationLevel === 'item' && (form.type === 'percentage-off' || form.type === 'flat-dollar-off')) && (
              <div data-error-key="minCheckSubtotal">
                <NumberField
                  label="Minimum Order Subtotal"
                  value={form.eligibility.minCheckSubtotal ?? 0}
                  placeholder="0"
                  onChange={(v) => updateEligibility('minCheckSubtotal', Number(v) || null)}
                  prefix="$"
                  min={0}
                  maxIntegerDigits={4}
                  maxDecimals={2}
                  helpText="Leave 0 for no minimum."
                  error={errors.minCheckSubtotal}
                />
              </div>
            )}

            {/* Item eligibility — only shown for Item Level */}
            {form.applicationLevel === 'item' && (
              <>
                <RadioGroup
                  label="Item Eligibility"
                  options={[
                    { value: 'all', label: 'All items eligible' },
                    { value: 'only', label: 'Only specific items' },
                    { value: 'except', label: 'All items except...' },
                  ]}
                  value={
                    showRequiredItems ? 'only' : showExcludedItems ? 'except' : 'all'
                  }
                  onChange={(v) => {
                    setShowRequiredItems(v === 'only');
                    setShowExcludedItems(v === 'except');
                    // Clear the other list when switching modes
                    if (v === 'only') updateEligibility('excludedItemIds', []);
                    if (v === 'except') updateEligibility('requiredItemIds', []);
                    if (v === 'all') {
                      updateEligibility('requiredItemIds', []);
                      updateEligibility('excludedItemIds', []);
                    }
                  }}
                />

                {showRequiredItems && (
                  <div data-error-key="requiredItems" style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid var(--divider)' }}>
                    <CategoryItemPicker
                      mode="item"
                      menuItems={menuItems}
                      selectedIds={form.eligibility.requiredItemIds}
                      onChange={(ids) => updateEligibility('requiredItemIds', ids)}
                      label="Eligible Items"
                    />
                    {errors.requiredItems && <span style={{ fontSize: 11, color: 'var(--red)', marginTop: 4, display: 'block' }}>{errors.requiredItems}</span>}
                  </div>
                )}

                {showExcludedItems && (
                  <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid var(--divider)' }}>
                    <CategoryItemPicker
                      mode="item"
                      menuItems={menuItems}
                      selectedIds={form.eligibility.excludedItemIds}
                      onChange={(ids) => updateEligibility('excludedItemIds', ids)}
                      label="Excluded Items"
                    />
                  </div>
                )}
              </>
            )}

            <div data-error-key="maxRedemptionsTotal" style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <NumberField
                label="Max Total Redemptions"
                value={form.eligibility.maxRedemptionsTotal ?? 0}
                placeholder="0"
                onChange={(v) => updateEligibility('maxRedemptionsTotal', Number(v) || null)}
                min={0}
                maxIntegerDigits={4}
                maxDecimals={0}
                helpText="0 = unlimited"
                error={errors.maxRedemptionsTotal}
              />
              {form.applicationLevel !== 'item' && !form.eligibility.firstTimeOnly && (
                <NumberField
                  label="Max Per Customer"
                  value={form.eligibility.maxRedemptionsPerCustomer ?? 0}
                  placeholder="0"
                  onChange={(v) => updateEligibility('maxRedemptionsPerCustomer', Number(v) || null)}
                  min={0}
                  maxIntegerDigits={4}
                  maxDecimals={0}
                  helpText="0 = unlimited"
                  error={errors.maxRedemptionsPerCustomer}
                />
              )}
              {form.applicationLevel !== 'item' && (form.eligibility.maxRedemptionsPerCustomer ?? 0) > 0 && (
                <Select
                  label="Period"
                  value={form.eligibility.redemptionPeriod}
                  onChange={(v) => updateEligibility('redemptionPeriod', v as OfferEligibility['redemptionPeriod'])}
                  options={REDEMPTION_PERIOD_OPTIONS}
                />
              )}
            </div>
          </FormSection>
        </Card>
      )}

      {/* ═══ Card 3: Schedule & Locations (Essential) ═══ */}
      <Card>
        <SectionLabel title="Schedule & Locations" />
        <p className="offer-form-section-desc">When and where this offer is valid.</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: form.schedule.noEndDate ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <TextField
              label="Start Date"
              type="date"
              value={form.schedule.startDate}
              onChange={(v) => updateSchedule('startDate', v)}
              min={new Date().toLocaleDateString('en-CA')}
              required
              error={errors.startDate}
            />
            {!form.schedule.noEndDate && (
              <TextField
                label="End Date"
                type="date"
                value={form.schedule.endDate || ''}
                onChange={(v) => updateSchedule('endDate', v || null)}
                min={form.schedule.startDate || undefined}
                required
                error={errors.endDate}
              />
            )}
          </div>

          {form.schedule.startDate && form.schedule.startDate < new Date().toLocaleDateString('en-CA') && (
            <div style={{ fontSize: 11, color: '#3B82F6' }}>
              ℹ️ Start date is in the past. The offer will be effective immediately upon activation.
            </div>
          )}

          {form.schedule.startDate > new Date().toLocaleDateString('en-CA') && form.status === 'draft' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#22C55E" strokeWidth="1.5"/><path d="M8 5V8.5L10 10" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <span style={{ fontSize: 12, color: '#16A34A', fontWeight: 500 }}>Scheduled to auto-activate on {form.schedule.startDate}</span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: 'var(--t2)' }}>No end date (runs indefinitely)</span>
            <Toggle checked={form.schedule.noEndDate} onChange={(v) => { updateSchedule('noEndDate', v); if (v) updateSchedule('endDate', null); }} />
          </div>
        </div>

        {/* Locations (essential for multi-location) */}
        {locations.length > 1 && (
        <FormSection title="Locations" description="Select which locations this offer applies to.">
          {(() => {
            const isAllScope = form.locationScope === 'all';
            const allSelected = isAllScope || form.locationIds.length === locations.length;
            const selectedCount = allSelected ? locations.length : form.locationIds.length;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, cursor: 'pointer', padding: '10px 14px', borderRadius: 8, border: allSelected ? '1px solid var(--accent)' : '1px solid var(--card-border)', background: allSelected ? 'var(--accent-soft, rgba(210,58,59,0.04))' : 'var(--surface)', transition: 'all 0.15s ease' }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (allSelected) {
                      update('locationScope', 'specific');
                      update('locationIds', []);
                    } else {
                      update('locationScope', 'all');
                      update('locationIds', locations.map((l) => l.id));
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, border: allSelected ? 'none' : '2px solid var(--card-border)', backgroundColor: allSelected ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {allSelected && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>All Locations</span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--t3)' }}>{selectedCount} of {locations.length} selected</span>
                </label>
                {!allSelected && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 8 }}>
                    {locations.map((loc) => {
                      const checked = form.locationIds.includes(loc.id);
                      return (
                        <label key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '7px 12px', borderRadius: 8, border: checked ? '1px solid var(--accent)' : '1px solid var(--card-border)', background: checked ? 'var(--accent-soft, rgba(210,58,59,0.04))' : 'var(--surface)', transition: 'all 0.15s ease' }}>
                          <input type="checkbox" checked={checked} onChange={(e) => {
                            const next = e.target.checked ? [...form.locationIds, loc.id] : form.locationIds.filter((id) => id !== loc.id);
                            update('locationIds', next);
                          }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                          <div style={{ width: 16, height: 16, borderRadius: 3, border: checked ? 'none' : '2px solid var(--card-border)', backgroundColor: checked ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {checked && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--t1)' }}>{loc.name}</span>
                          {loc.franchiseOwnerId && <span style={{ fontSize: 10, color: 'var(--t3)', background: 'var(--hover)', padding: '2px 6px', borderRadius: 4 }}>Franchise</span>}
                        </label>
                      );
                    })}
                    {errors.locationIds && <span style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>{errors.locationIds}</span>}
                  </div>
                )}
              </div>
            );
          })()}
        </FormSection>
        )}
      </Card>

      {/* ═══ Internal Notes (separate container) ═══ */}
      <Card>
        <SectionLabel title="Internal Notes" />
        <p className="offer-form-section-desc">Visible to Back Office users only — never shown to customers.</p>
        <TextArea
          label=""
          value={form.internalNotes}
          onChange={(v) => update('internalNotes', v)}
          maxLength={500}
          rows={3}
          helpText="Visible to Back Office users only."
        />
      </Card>

      {/* ═══ Advanced Settings (Collapsible) ═══ */}
      <Card>
        <div
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
        >
          <div>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', letterSpacing: '-0.01em' }}>Advanced Settings</span>
            <p style={{ fontSize: 12, color: 'var(--t3)', margin: '2px 0 0', lineHeight: 1.5 }}>Order types, stacking, application mode and eligibility rules.</p>
          </div>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="var(--t3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, transition: 'transform 0.2s', transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>

        {showAdvanced && (
          <div ref={advancedContentRef} style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 0 }}>

        <div data-error-key="orderTypes">
        {form.type === 'free-delivery' ? (
        <div style={{ borderBottom: '1px solid var(--divider)', paddingBottom: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--blue, #3B82F6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
            <span style={{ fontSize: 12, color: 'var(--t2)' }}>This offer applies only to <strong>Online Delivery</strong> orders.</span>
          </div>
        </div>
        ) : (
        <FormSection title="Order Types" description="Select which order types this offer applies to.">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, alignItems: 'start' }}>
            {/* ── POS (Staff) Orders group ── */}
            {branchPosOrderTypes.length > 0 && (() => {
              const checkedPOS = branchPosOrderTypes.filter((o) => form.orderTypes.includes(o.value));
              const allPOSChecked = checkedPOS.length === branchPosOrderTypes.length;
              const somePOSChecked = checkedPOS.length > 0 && !allPOSChecked;
              return (
                <div>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--divider)', marginBottom: 6 }}
                    onClick={(e) => {
                      e.preventDefault();
                      const without = form.orderTypes.filter((ot) => !branchAllPos.includes(ot));
                      update('orderTypes', allPOSChecked ? without : [...without, ...branchAllPos]);
                    }}
                  >
                    <div style={{ width: 18, height: 18, borderRadius: 4, border: allPOSChecked || somePOSChecked ? 'none' : '2px solid var(--card-border, #E2E5EC)', backgroundColor: allPOSChecked || somePOSChecked ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s ease' }}>
                      {allPOSChecked && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                      {somePOSChecked && <div style={{ width: 8, height: 2, background: '#fff', borderRadius: 1 }} />}
                    </div>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>POS Orders</span>
                      <span style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 8 }}>Staff-operated</span>
                    </div>
                  </label>
                  <div style={{ paddingLeft: 28, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {branchPosOrderTypes.map((opt) => {
                      const checked = form.orderTypes.includes(opt.value);
                      return (
                        <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '7px 12px', borderRadius: 8, border: checked ? '1px solid var(--accent)' : '1px solid var(--card-border, #E2E5EC)', backgroundColor: checked ? 'var(--accent-soft, rgba(210,58,59,0.04))' : 'var(--surface)', transition: 'all 0.15s ease' }}>
                          <div style={{ width: 16, height: 16, borderRadius: 3, border: checked ? 'none' : '2px solid var(--card-border, #E2E5EC)', backgroundColor: checked ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {checked && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                          <input type="checkbox" checked={checked} onChange={() => {
                            const next = checked ? form.orderTypes.filter((v) => v !== opt.value) : [...form.orderTypes, opt.value];
                            update('orderTypes', next);
                          }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--t1)' }}>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ── Customer-Facing Orders group ── */}
            {branchCustomerOrderTypes.length > 0 && (() => {
              const checkedCustomer = branchCustomerOrderTypes.filter((o) => form.orderTypes.includes(o.value));
              const allCustomerChecked = checkedCustomer.length === branchCustomerOrderTypes.length;
              const someCustomerChecked = checkedCustomer.length > 0 && !allCustomerChecked;
              return (
                <div>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--divider)', marginBottom: 6 }}
                    onClick={(e) => {
                      e.preventDefault();
                      const without = form.orderTypes.filter((ot) => !branchAllCustomer.includes(ot));
                      update('orderTypes', allCustomerChecked ? without : [...without, ...branchAllCustomer]);
                    }}
                  >
                    <div style={{ width: 18, height: 18, borderRadius: 4, border: allCustomerChecked || someCustomerChecked ? 'none' : '2px solid var(--card-border, #E2E5EC)', backgroundColor: allCustomerChecked || someCustomerChecked ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s ease' }}>
                      {allCustomerChecked && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                      {someCustomerChecked && <div style={{ width: 8, height: 2, background: '#fff', borderRadius: 1 }} />}
                    </div>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>Customer Orders</span>
                      <span style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 8 }}>Self-service</span>
                    </div>
                  </label>
                  <div style={{ paddingLeft: 28, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {branchCustomerOrderTypes.map((opt) => {
                      const checked = form.orderTypes.includes(opt.value);
                      return (
                        <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '7px 12px', borderRadius: 8, border: checked ? '1px solid var(--accent)' : '1px solid var(--card-border, #E2E5EC)', backgroundColor: checked ? 'var(--accent-soft, rgba(210,58,59,0.04))' : 'var(--surface)', transition: 'all 0.15s ease' }}>
                          <div style={{ width: 16, height: 16, borderRadius: 3, border: checked ? 'none' : '2px solid var(--card-border, #E2E5EC)', backgroundColor: checked ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {checked && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                          <input type="checkbox" checked={checked} onChange={() => {
                            const next = checked ? form.orderTypes.filter((v) => v !== opt.value) : [...form.orderTypes, opt.value];
                            update('orderTypes', next);
                          }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--t1)' }}>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
          {errors.orderTypes && <span style={{ fontSize: 11, color: 'var(--red)', marginTop: 4, display: 'block' }}>{errors.orderTypes}</span>}
        </FormSection>
        )}
        </div>

        {/* ═══ Stacking / Exclusivity ═══ */}
        {form.applicationLevel !== 'item' && (
        <div style={{ borderBottom: '1px solid var(--divider)', paddingBottom: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <span style={{ fontSize: 13, color: 'var(--t1)', fontWeight: 500 }}>Non-Stackable Offer</span>
              <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }} >
                {/* {form.isExclusive ? 'This offer cannot be combined with any other offer on the same order.' : 'This offer can stack with other non-exclusive offers.'} */}
               <p style={{margin:0}}>Enable to prevent this offer from being combined with other offers.</p> 
               <p style={{margin:0,marginTop:"5px"}}>Disable to allow this offer to be combined with other offers.</p>

              </div>
            </div>
            <Toggle checked={form.isExclusive} onChange={(v) => update('isExclusive', v)} />
          </div>
        </div>
        )}

        {/* Application Mode — hidden for types that are always auto-apply */}
        {form.type !== 'free-item' && form.type !== 'free-delivery' && (
           <FormSection title="Application Mode" description="How the offer is applied. Order Types above control where it appears.">
          {/* Auto-Apply toggle \u2014 hidden for item-level percentage/flat offers */}
          {!(form.applicationLevel === 'item' && (form.type === 'percentage-off' || form.type === 'flat-dollar-off')) && (
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--divider, #E8EAF0)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>Auto-Apply</div>
                <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
                  {form.applicationModes.inStore === 'auto-apply' || form.applicationModes.inStore === 'auto-apply-promo'
                    ? 'Automatically applies when order meets conditions. No action needed.'
                    : 'Off \u2014 must be manually selected. Staff applies from POS; customers select from available offers online.'}
                </div>
              </div>
              <Toggle
                checked={form.applicationModes.inStore === 'auto-apply' || form.applicationModes.inStore === 'auto-apply-promo'}
                onChange={(v) => {
                  const mode = v ? 'auto-apply' : 'manual';
                  update('applicationModes', { inStore: mode as ApplicationMode, digital: mode as ApplicationMode });
                }}
              />
            </div>
          )}

          {/* Promo Code toggle */}
          <div data-error-key="promoCodes" style={{ padding: '12px 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>Promo Code</div>
                <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>Customers enter code at online checkout or kiosk. Staff enters code at POS on behalf of customer.</div>
              </div>
              <Toggle
                checked={requirePromoCode}
                onChange={(v) => setRequirePromoCode(v)}
              />
            </div>

            {/* Promo Code list — shown when toggle is on */}
            {requirePromoCode && (
              <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: '2px solid var(--accent-soft, rgba(210,58,59,0.15))' }}>
                {form.promoCodes.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                    {form.promoCodes.map((pc) => (
                      <div key={pc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: 'var(--bg, #F5F6FA)' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.5px', flex: 1 }}>
                          {pc.code}
                        </span>
                        {/* Single-use checkbox hidden for now
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--t2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={pc.singleUse}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updated = form.promoCodes.map((c) => {
                                if (c.id !== pc.id) return c;
                                // FE contract: single-use is represented by max_usage = 1.
                                // When user unchecks single-use from an auto-populated max_usage=1 code,
                                // reset maxRedemptions so update payload can send max_usage != 1.
                                if (!checked && c.maxRedemptions === 1) {
                                  return { ...c, singleUse: false, maxRedemptions: 0 };
                                }
                                return { ...c, singleUse: checked };
                              });
                              update('promoCodes', updated);
                            }}
                            style={{ width: 13, height: 13, accentColor: 'var(--accent)' }}
                          />
                          Single-use
                        </label>
                        */}
                        <button
                          type="button"
                          onClick={() => update('promoCodes', form.promoCodes.filter((c) => c.id !== pc.id))}
                          style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 25, padding: 0, lineHeight: 1 }}
                        >&times;</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, maxWidth: 320 }}>
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => {
                      const sanitized = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9-]/g, '')
                        .slice(0, 20);
                      setPromoInput(sanitized);
                      if (promoError) setPromoError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPromoCode();
                      }
                    }}
                    placeholder="e.g., SAVE10"
                    maxLength={20}
                    style={{
                      fontSize: 13, fontFamily: 'monospace', padding: '8px 12px',
                      border: promoError ? '1px solid var(--red, #E5484D)' : '1px solid var(--card-border)', borderRadius: 8,
                      outline: 'none', width: 160, textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddPromoCode}
                    disabled={!hasAlphanumeric(promoInput)}
                    style={{
                      padding: '8px 14px', fontSize: 12, fontWeight: 500,
                      color: promoInput.trim() ? 'var(--accent)' : 'var(--t3)',
                      background: 'none', cursor: promoInput.trim() ? 'pointer' : 'default',
                      border: '1px solid var(--divider)', borderRadius: 8,
                    }}
                  >
                    + Add
                  </button>
                </div>
                {(promoError || errors.promoCodes) && (
                  <div style={{ fontSize: 11, color: 'var(--red, #E5484D)', fontWeight: 500, marginTop: 4 }}>
                    {promoError || errors.promoCodes}
                  </div>
                )}
                {!promoError && !errors.promoCodes && form.promoCodes.length === 0 && (
                  <div style={{ fontSize: 11, color: 'var(--t3)', fontStyle: 'italic', marginTop: 4 }}>
                    Add at least one promo code.
                  </div>
                )}
              </div>
            )}
          </div>
        </FormSection>
        )}

        {/* Visibility — order-level offers only */}
        {form.applicationLevel === 'order' && (
        <FormSection title="Offer Visibility" description="Choose whether this offer is shown on ordering channels.">
          <RadioGroup
            label=""
            value={form.isHidden ? 'hidden' : 'public'}
            onChange={(v) => update('isHidden', v === 'hidden')}
            options={VISIBILITY_OPTIONS}
          />
          {form.isHidden && (
            <div style={S.infoBox}>
              <span className="material-symbols-rounded" style={{ fontSize: 18, color: 'var(--accent, #D23A3B)', flexShrink: 0, marginTop: 1 }}>
                info
              </span>
              <div style={{ marginTop: 2, fontSize: 12, color: 'var(--t2)' }}>
                This offer won't be shown anywhere, but can still be redeemed with the coupon code.
              </div>
            </div>
          )}
        </FormSection>
        )}

        {/* ── Recurring Schedule ── */}
        {/* <div data-error-key="recurringDays" style={{ borderBottom: '1px solid var(--divider)', paddingBottom: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: 'var(--t2)' }}>Restrict to specific days/times</span>
            <Toggle checked={showRecurring} onChange={(v) => { setShowRecurring(v); if (v) { if (!form.schedule.recurring) updateSchedule('recurring', { days: [], allDay: true, startTime: '00:00', endTime: '23:45' }); } else { updateSchedule('recurring', null); } }} />
          </div>

          {showRecurring && (
            <div style={{ marginTop: 12, paddingLeft: 8, borderLeft: '2px solid var(--divider)' }}>
              <CheckboxGroup
                label="Days of Week"
                value={(form.schedule.recurring?.days || []).map(String)}
                onChange={(vals) => updateSchedule('recurring', { ...(form.schedule.recurring || { days: [], allDay: true, startTime: '00:00', endTime: '23:45' }), days: vals.map(Number) })}
                options={DAY_OPTIONS}
                inline
              />
              {errors.recurringDays && <span style={{ fontSize: 11, color: 'var(--red)' }}>{errors.recurringDays}</span>}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--t2)' }}>All day (no time restriction)</span>
                <Toggle
                  checked={form.schedule.recurring?.allDay ?? true}
                  onChange={(v) => updateSchedule('recurring', { ...(form.schedule.recurring || { days: [], allDay: true, startTime: '00:00', endTime: '23:45' }), allDay: v })}
                />
              </div>

              {!form.schedule.recurring?.allDay && (
                <div data-error-key="recurringTime" style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <Select
                    label="Start Time"
                    value={form.schedule.recurring?.startTime || '00:00'}
                    onChange={(v) => {
                      updateSchedule('recurring', { ...(form.schedule.recurring || { days: [], allDay: false, startTime: '00:00', endTime: '23:45' }), startTime: v });
                      setErrors((prev) => { const next = { ...prev }; delete next.recurringTime; return next; });
                    }}
                    options={TIME_OPTIONS}
                  />
                  <Select
                    label="End Time"
                    value={form.schedule.recurring?.endTime || '23:45'}
                    onChange={(v) => {
                      updateSchedule('recurring', { ...(form.schedule.recurring || { days: [], allDay: false, startTime: '00:00', endTime: '23:45' }), endTime: v });
                      setErrors((prev) => { const next = { ...prev }; delete next.recurringTime; return next; });
                    }}
                    options={TIME_OPTIONS}
                    error={errors.recurringTime}
                  />
                </div>
              )}
            </div>
          )}
        </div> */}

        {/* ── Eligibility ── */}

        {form.applicationLevel !== 'item' && (
        <div style={{ borderBottom: '1px solid var(--divider)', paddingBottom: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <span style={{ fontSize: 13, color: 'var(--t1)', fontWeight: 500 }}>First-time customers only</span>
              <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
                {form.eligibility.firstTimeOnly
                  ? 'Only customers with no prior completed orders.'
                  : 'Available to all customers.'}
              </div>
            </div>
            <Toggle
              checked={form.eligibility.firstTimeOnly}
              onChange={(v) => {
                updateEligibility('firstTimeOnly', v);
                if (v) {
                  updateEligibility('maxRedemptionsTotal', null);
                  updateEligibility('maxRedemptionsPerCustomer', null);
                }
              }}
            />
          </div>
        </div>
        )}

          </div>
        )}
      </Card>

      {/* ═══ Rule Summary (Always Visible) ═══ */}
      <Card>
        <FormSection title="Rule Summary" description="Review all eligibility rules at a glance.">
          <div style={{
            background: 'var(--bg)',
            borderRadius: 10,
            border: '1px solid var(--divider)',
            overflow: 'hidden',
          }}>
            {summaryRows.map((row, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 14px',
                borderBottom: i < summaryRows.length - 1 ? '1px solid var(--divider)' : 'none',
                background: row.warn ? 'rgba(234, 179, 8, 0.06)' : 'transparent',
              }}>
                <SummaryIcon type={row.iconKey} color={row.warn ? 'var(--orange)' : 'var(--accent)'} />
                <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--t3)',
                  minWidth: 90,
                  flexShrink: 0,
                  lineHeight: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}>{row.label}</span>
                <span style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: row.warn ? 'var(--orange)' : 'var(--t1)',
                  lineHeight: '20px',
                  wordBreak: 'break-word',
                }}>
                  {row.value}
                  {row.warn && <span style={{ fontSize: 10, marginLeft: 6, color: 'var(--orange)' }}>(past date)</span>}
                </span>
              </div>
            ))}
            {summaryRows.length === 0 && (
              <div style={{ padding: '14px', fontSize: 12, color: 'var(--t3)', textAlign: 'center' }}>
                No restrictions — applies to all eligible orders.
              </div>
            )}
          </div>
          {conflicts.length > 0 && (
            <div style={{
              marginTop: 10,
              padding: '10px 14px',
              background: 'rgba(220, 38, 38, 0.05)',
              borderRadius: 8,
              border: '1px solid rgba(220, 38, 38, 0.15)',
            }}>
              {conflicts.map((c, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--red)', fontWeight: 500, marginBottom: i < conflicts.length - 1 ? 4 : 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>⚠️</span> {c}
                </div>
              ))}
            </div>
          )}
          {narrowAdvisory && (
            <div style={{
              marginTop: 10,
              padding: '10px 14px',
              background: 'rgba(234, 179, 8, 0.06)',
              borderRadius: 8,
              border: '1px solid rgba(234, 179, 8, 0.2)',
              fontSize: 11,
              color: 'var(--orange)',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span>ℹ️</span> {narrowAdvisory}
            </div>
          )}
        </FormSection>

      </Card>

      {/* ═══ Sticky Action Bar ═══ */}
      <div className="offer-form-sticky-bar">
        {isDirty && (
          <span className="offer-form-unsaved">
            ● Unsaved changes
          </span>
        )}
        {isEditing && !isActiveOffer && (
          <Button
            variant="ghost"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving || deleting}
            className="offer-form-delete-btn"
          >
            Delete Offer
          </Button>
        )}
        <Button variant="ghost" onClick={handleCancel} disabled={saving || deleting}>Cancel</Button>
        {!(isEditing && isActiveOffer) && (
        <Button variant="secondary" onClick={() => handleSave('draft')} loading={savingAction === 'draft'} disabled={saving || deleting}>
          {isEditing ? 'Save Changes' : 'Save as Draft'}
        </Button>
        )}
        <Button variant="primary" onClick={() => handleSave('active')} loading={savingAction === 'active'} disabled={saving || deleting || conflicts.length > 0}>
          {isEditing && isActiveOffer ? 'Save & Keep Active' : 'Save & Activate'}
        </Button>
      </div>

      {/* Type change confirmation */}
      <ConfirmDialog
        open={showTypeChangeConfirm}
        onClose={() => { setShowTypeChangeConfirm(false); setPendingType(null); }}
        onConfirm={() => {
          if (pendingType) applyTypeChange(pendingType);
          setShowTypeChangeConfirm(false);
          setPendingType(null);
        }}
        title="Change Offer Type"
        message="Changing the offer type will reset type-specific settings. Continue?"
        confirmText="Continue"
      />

      {/* Active offer edit confirmation (1.7 AC4) */}
      <ConfirmDialog
        open={showActiveConfirm}
        onClose={() => { setShowActiveConfirm(false); setPendingSaveStatus(null); }}
        onConfirm={() => {
          if (pendingSaveStatus) executeSave(pendingSaveStatus);
          setShowActiveConfirm(false);
          setPendingSaveStatus(null);
        }}
        title="Update Active Offer"
        message="This offer is currently active. Changes will take effect immediately on all order types and apply to future redemptions only. Continue?"
        confirmText="Save Changes"
        variant="warning"
      />

      {/* Delete offer confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          void handleDelete();
        }}
        title="Delete Offer"
        message="Permanently delete this offer? This cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      {/* Unsaved changes confirmation (cancel / browser back / sidebar nav) */}
      <ConfirmDialog
        open={showDiscardModal}
        onClose={handleKeepEditing}
        onConfirm={handleConfirmDiscard}
        title="Discard changes?"
        message="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        confirmText="Discard"
        cancelText="Keep Editing"
        variant="danger"
      />
    </div>
    </DesktopLayout>
  );
}
